import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * The marks `useHeaderFit` finds its way around the toolbar by.
 *
 * Attributes rather than a ref per element, and that is a deliberate trade.
 * The hook needs seven measurements spread across three components, and
 * threading seven refs down through `SiteHeader` into `HeaderNav` and
 * `HeaderActions` would put the measuring apparatus into the props of every
 * component it passes through — each of which would then be partly about
 * measurement rather than wholly about what it draws. One ref on the row and a
 * named mark on each thing worth measuring keeps that cost in this file.
 *
 * They are exported so the components that wear them cannot misspell them.
 */
export const FIT = {
  /** The nav element itself: the one toolbar child allowed to give up width. */
  nav: 'data-kbx-fit-nav',
  /** The right-hand control cluster. */
  actions: 'data-kbx-fit-actions',
  /** The secondary CTA, while it is on the row. */
  cta: 'data-kbx-fit-cta',
  /** The hidden row every natural width is read from. */
  ghost: 'data-kbx-fit-ghost',
  /** One hidden twin per nav item, in `primaryNav` order. */
  ghostItem: 'data-kbx-fit-ghost-item',
  /** The hidden twin of the "More" pill. */
  ghostMore: 'data-kbx-fit-ghost-more',
  /** The hidden twin of the secondary CTA. */
  ghostCta: 'data-kbx-fit-ghost-cta',
} as const;

export type FitMark = (typeof FIT)[keyof typeof FIT];

/**
 * Spreads one of the marks above onto an element.
 *
 * A helper rather than a literal `data-…` attribute at each call site, so the
 * selectors in this file and the attributes in the components are the same
 * strings by construction and cannot be fixed in one place only.
 */
export const fitMark = (mark: FitMark): Record<string, string> => ({ [mark]: '' });

/**
 * Items that never fold, however tight the row gets.
 *
 * The first three are "Car Finder", "Brands" and "Services" — the two
 * mega-menus and the page the whole site is pointed at. A header that has
 * folded those away has stopped being a header, and both mega-menus carry
 * panels (a brand-tile grid, three columns of services) that do not survive
 * being nested inside another panel. Three items, a "More" pill and the
 * controls fit inside 1,200px with room to spare even at 200% text size, so
 * this floor is never the thing that overflows.
 */
const MIN_INLINE_ITEMS = 3;

/**
 * Slack left at the end of the row, in px.
 *
 * Not a fudge factor for bad measurements — the arithmetic below is exact.
 * It is the gap between the last pill and the first control, which at zero
 * would have the nav touching the theme toggle the moment the fit is tight.
 */
const BREATHING_ROOM = 12;

export type HeaderFit = {
  /** Goes on the toolbar: the row whose width everything is measured against. */
  rowRef: RefObject<HTMLDivElement | null>;
  /** How many of the primary nav items render inline; the rest fold into "More". */
  visibleCount: number;
  /** Whether the outlined secondary CTA has earned its place on the row. */
  showSecondaryCta: boolean;
};

type Plan = Pick<HeaderFit, 'visibleCount' | 'showSecondaryCta'>;

const width = (el: Element | null) => (el ? el.getBoundingClientRect().width : 0);

const gapOf = (el: Element) => parseFloat(getComputedStyle(el).columnGap) || 0;

/**
 * Picks the richest arrangement that fits, given what everything measures.
 *
 * Pure, and separated from the DOM reading above it so the ladder can be
 * reasoned about — and argued with — without a browser. The order of the
 * three rungs is a product decision, not an implementation detail:
 *
 * 1. **Everything.** Seven items and both calls to action.
 * 2. **Drop the secondary CTA.** "Get Free Advice" is the first thing to go,
 *    because it is the only control on the row that has somewhere else to
 *    live: it is the second item in the mobile drawer and it is in the
 *    footer's Company column on every page. A nav item that folds is a page
 *    that costs one extra click to reach; this pill that folds costs nothing.
 * 3. **Fold the tail into "More".** Last resort, from the right, never past
 *    `MIN_INLINE_ITEMS`. On the sizes this header actually ships at, rung 3 is
 *    unreachable — it exists so that a longer label, an eighth menu or a
 *    visitor running large text cannot put "Get a Quote" off the screen again,
 *    which is exactly how this bug arrived the first time.
 *
 * Note that rung 2 never un-drops the CTA to pay for rung 3. Once the row is
 * tight enough to be folding nav items, a second call to action is not the
 * thing it is short of.
 */
export function planHeaderFit(input: {
  /** Width the nav and the secondary CTA have to share. */
  available: number;
  /** Natural width of each nav item, in row order. */
  itemWidths: readonly number[];
  /** Natural width of the "More" pill. */
  moreWidth: number;
  /** Natural width of the secondary CTA, plus the gap that precedes it. */
  ctaWidth: number;
  /** Gap between nav pills. */
  navGap: number;
}): Plan {
  const { available, itemWidths, moreWidth, ctaWidth, navGap } = input;
  const count = itemWidths.length;
  const runTo = (n: number) =>
    n <= 0 ? 0 : itemWidths.slice(0, n).reduce((a, b) => a + b, 0) + navGap * (n - 1);

  const all = runTo(count);
  if (all + ctaWidth <= available) return { visibleCount: count, showSecondaryCta: true };
  if (all <= available) return { visibleCount: count, showSecondaryCta: false };

  for (let n = count - 1; n >= MIN_INLINE_ITEMS; n -= 1) {
    if (runTo(n) + navGap + moreWidth <= available) {
      return { visibleCount: n, showSecondaryCta: false };
    }
  }
  return { visibleCount: Math.min(MIN_INLINE_ITEMS, count), showSecondaryCta: false };
}

/** Measuring before paint is the point; on the prerender pass there is none. */
const useMeasureEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Keeps the header's top row inside the screen by measuring it rather than by
 * guessing at breakpoints.
 *
 * ## Why this is measured and not a media query
 *
 * The row is a sum of things no breakpoint knows about: seven labels whose
 * widths depend on the font that actually loaded, a quotation-list badge that
 * appears only once a visitor has saved a car, and a visitor's own text-size
 * setting, which moves every label without moving the viewport an inch. A
 * breakpoint that fits today is a breakpoint that is wrong the next time
 * somebody renames a menu — which is how "Get a Quote" ended up 38px off the
 * right edge of a 1,200px laptop in the first place, three weeks after the row
 * had been tightened to fit.
 *
 * So the hook asks the row how wide it is, asks the items how wide they are,
 * and takes the richest arrangement that fits. `navItemSx` still sets the
 * comfortable size; this decides what survives when comfortable is too big.
 *
 * ## Why it cannot oscillate
 *
 * The usual failure of a measure-and-fold layout is a feedback loop: hiding
 * something frees space, the free space says it fits, it comes back, and the
 * observer runs forever. Two things rule that out here.
 *
 * The widths come from a **hidden twin of the full row** (`HeaderNavMeasure`),
 * not from what is currently on screen, so an item's natural width is readable
 * whether or not it is folded away. Nothing has to be shown to be measured.
 *
 * And the space those widths are compared against is computed from quantities
 * the plan cannot change: the toolbar's inner width, the logo, and the control
 * cluster **with the secondary CTA's own width taken back out of it**. Folding
 * a nav item does not move it, and dropping the CTA does not move it. The plan
 * is a pure function of inputs it has no influence over, which is a stronger
 * guarantee than a debounce or a hysteresis band would give.
 *
 * ## What it watches
 *
 * The row (the window resized), the control cluster (the quotation badge
 * arrived, so the nav has less room than it did a second ago) and the hidden
 * twin (the web font finally swapped in and every label is now a different
 * width). One `ResizeObserver` covers all three. `document.fonts.ready` is
 * belt and braces for the browser that swaps a font without resizing anything
 * the observer is watching.
 *
 * Below `lg` the nav is `display: none`, the twin measures zero, and the hook
 * returns without touching state — the drawer owns those widths and there is
 * nothing to decide.
 */
export function useHeaderFit(itemCount: number): HeaderFit {
  const rowRef = useRef<HTMLDivElement>(null);
  /** Everything visible, which is also what the prerendered HTML contains. */
  const [plan, setPlan] = useState<Plan>({ visibleCount: itemCount, showSecondaryCta: true });

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;

    const nav = row.querySelector(`[${FIT.nav}]`);
    const actions = row.querySelector(`[${FIT.actions}]`);
    const ghost = row.querySelector(`[${FIT.ghost}]`);
    if (!nav || !actions || !ghost) return;

    const itemWidths = [...ghost.querySelectorAll(`[${FIT.ghostItem}]`)].map(width);
    // Zero means the nav is `display: none` — below `lg`, where the drawer
    // carries the navigation and there is nothing for this hook to decide.
    if (itemWidths.length === 0 || itemWidths[0] === 0) return;

    const rowStyle = getComputedStyle(row);
    const inner =
      row.clientWidth -
      parseFloat(rowStyle.paddingLeft) -
      parseFloat(rowStyle.paddingRight) -
      gapOf(row) * Math.max(row.children.length - 1, 0);

    const actionsGap = gapOf(actions);
    const liveCta = row.querySelector(`[${FIT.cta}]`);
    // The cluster as it would measure with no secondary CTA in it. Taking the
    // pill back out is what makes `available` independent of the plan, and so
    // what makes the plan stable.
    const clusterWithoutCta = width(actions) - (liveCta ? width(liveCta) + actionsGap : 0);
    const siblings = [...row.children].reduce(
      (total, child) => (child === nav || child === actions ? total : total + width(child)),
      0,
    );

    const next = planHeaderFit({
      available: inner - siblings - clusterWithoutCta - BREATHING_ROOM,
      itemWidths,
      moreWidth: width(ghost.querySelector(`[${FIT.ghostMore}]`)),
      ctaWidth: width(ghost.querySelector(`[${FIT.ghostCta}]`)) + actionsGap,
      navGap: gapOf(nav),
    });

    setPlan((current) =>
      current.visibleCount === next.visibleCount &&
      current.showSecondaryCta === next.showSecondaryCta
        ? current
        : next,
    );
  }, []);

  useMeasureEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(row);
    for (const mark of [FIT.actions, FIT.ghost]) {
      const el = row.querySelector(`[${mark}]`);
      if (el) observer.observe(el);
    }

    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) measure();
    });

    return () => {
      live = false;
      observer.disconnect();
    };
  }, [measure]);

  return { rowRef, ...plan };
}
